import type { AccountExtension, Fetched } from "./platform-client.js";

interface Dependencies {
  list(): Promise<Fetched<{ extensions: readonly AccountExtension[] }>>;
  set(id: string, enabled: boolean, changeId: string): Promise<Fetched<unknown>>;
  newId(): string;
}
interface Attempt { enabled: boolean; changeId: string; busy: boolean; error?: string }

/** Account controls retain an uncertain write's identity until its retry succeeds. */
export function accountExtensions(container: HTMLElement, deps: Dependencies): { load(): Promise<void>; reset(): void } {
  let entries: readonly AccountExtension[] = [];
  const attempts = new Map<string, Attempt>();
  let epoch = 0;
  let loading = false;
  let message = "Open your account to load extensions.";
  let loadFailed = false;
  let focusToRestore: string | undefined;
  const doc = container.ownerDocument;

  function render(): void {
    const focused = (doc.activeElement as HTMLElement | null)?.dataset.extension;
    if (focused) focusToRestore = focused;
    else if (doc.activeElement !== doc.body) focusToRestore = undefined;
    container.replaceChildren();
    const status = doc.createElement("p");
    status.className = "fine-print";
    status.setAttribute("role", "status");
    status.textContent = message;
    container.append(status);
    if (loadFailed) {
      const retry = doc.createElement("button");
      retry.type = "button";
      retry.className = "secondary-link";
      retry.textContent = "Retry loading extensions";
      retry.addEventListener("click", () => { void load(); });
      container.append(retry);
    }
    for (const entry of entries) {
      const row = doc.createElement("div");
      row.className = "extension-row";
      const name = doc.createElement("strong");
      name.textContent = entry.title;
      const state = doc.createElement("span");
      state.textContent = entry.configurable ? (entry.enabled ? "On" : "Off") : "Always on";
      row.append(name, state);
      if (entry.configurable) {
        const attempt = attempts.get(entry.id);
        const desired = attempt?.enabled ?? !entry.enabled;
        const action = `${desired ? "Turn on" : "Turn off"} ${entry.title}`;
        const button = doc.createElement("button");
        button.type = "button";
        button.className = "secondary-link";
        button.dataset.extension = entry.id;
        button.textContent = attempt?.busy ? "Saving…" : attempt?.error ? `Retry: ${action}` : action;
        button.disabled = loading || [...attempts.values()].some((value) => value.busy);
        button.addEventListener("click", () => { void toggle(entry); });
        row.append(button);
        if (attempt?.error) {
          const error = doc.createElement("p");
          error.className = "fine-print extension-error";
          error.setAttribute("role", "alert");
          error.textContent = attempt.error;
          row.append(error);
        }
      }
      container.append(row);
    }
    if (focusToRestore) container.querySelectorAll<HTMLButtonElement>("button[data-extension]").forEach((button) => {
      if (button.dataset.extension === focusToRestore && !button.disabled) {
        button.focus();
        focusToRestore = undefined;
      }
    });
  }

  async function load(): Promise<void> {
    if (loading || [...attempts.values()].some((attempt) => attempt.busy)) return;
    const current = epoch;
    loading = true;
    loadFailed = false;
    message = "Loading extensions…";
    render();
    const result = await deps.list();
    if (current !== epoch) return;
    loading = false;
    if (result.ok) {
      entries = result.value.extensions;
      message = entries.length ? "Choose the tools available to your assistant." : "No extensions are available for this account.";
    } else {
      message = result.detail;
      loadFailed = true;
    }
    render();
  }

  async function toggle(entry: AccountExtension): Promise<void> {
    if (loading || !entry.configurable || [...attempts.values()].some((value) => value.busy)) return;
    const existing = attempts.get(entry.id);
    if (existing?.busy) return;
    const attempt: Attempt = existing ?? { enabled: !entry.enabled, changeId: deps.newId(), busy: false };
    attempt.busy = true;
    delete attempt.error;
    attempts.set(entry.id, attempt);
    const current = epoch;
    render();
    const result = await deps.set(entry.id, attempt.enabled, attempt.changeId);
    if (current !== epoch) return;
    if (result.ok) {
      // A replay receipt describes the historical operation, not current state.
      const confirmed = await deps.list();
      if (current !== epoch) return;
      if (confirmed.ok) {
        entries = confirmed.value.extensions;
        attempts.delete(entry.id);
        const latest = entries.find((value) => value.id === entry.id);
        message = latest ? `${entry.title} is ${latest.enabled ? "on" : "off"}.` : "Extensions refreshed.";
      } else {
        attempt.busy = false;
        attempt.error = `Change accepted, but current state could not be confirmed. Retry to check. ${confirmed.detail}`;
      }
    } else {
      attempt.busy = false;
      attempt.error = result.detail;
    }
    render();
  }

  function reset(): void {
    epoch++;
    entries = [];
    attempts.clear();
    focusToRestore = undefined;
    loading = false;
    loadFailed = false;
    message = "Open your account to load extensions.";
    render();
  }
  render();
  return { load, reset };
}
